import React, { useMemo } from 'react'
import {
  Zoning,
  ZoningType,
  ZoningReportData,
  ZoningEstimateStats,
} from '@popety-io/types'
import {
  ZoneMetricsTable,
  METRIC_KEY_TO_TRANSLATION_KEY,
} from './ZoneMetricsTable'
import { ZoningDetailHeader } from './ZoningDetailHeader'
import { MetricsLegend, type MetricsLegendTranslations } from './MetricsLegend'
import { Card } from './ZoneCard.styles'
import type { ZoneQAService, ZoneQATranslations } from './ZoneQA'

export interface ZoneCardTranslations {
  unknownZone?: string
  legend?: MetricsLegendTranslations
  metricsTable?: {
    title?: string
    resetToDefault?: string
    editAuthorizedValues?: string
    saveChanges?: string
    cancel?: string
    confirmReset?: string
    headers?: {
      indices?: string
      current?: string
      authorized?: string
      difference?: string
    }
    groups?: {
      heights?: string
      floors?: string
      setbacks?: string
      usageRatios?: string
      environmental?: string
      other?: string
    }
    derivedSurfaces?: {
      determiningBuiltArea?: string
      determiningBuiltAreaDesc?: string
      builtArea?: string
      builtAreaDesc?: string
      determiningFloorArea?: string
      determiningFloorAreaDesc?: string
      grossUsefulFloorArea?: string
      grossUsefulFloorAreaDesc?: string
      floorArea?: string
      floorAreaDesc?: string
      builtVolume?: string
      builtVolumeDesc?: string
    }
  }
  sourceLabels?: {
    manual?: string
    geoportal?: string
    llm?: string
    estimate?: string
    old?: string
  }
  status?: {
    current?: string
    future?: string
    past?: string
  }
  header?: {
    unknownZone?: string
    buildingArea?: string
    nonBuildingArea?: string
    adopted?: string
    abrogated?: string
    purpose?: string
    constructionArrangement?: string
    constructionArrangementValues?: {
      C?: string
      NC?: string
      'C / NC'?: string
    }
    references?: string
    documents?: string
    /** Segmented-card toolbar labels */
    segments?: {
      potential?: string
      purposeRules?: string
      documents?: string
    }
    /** Suffix shown after the percentage in the plot-coverage pill (e.g., "of plot") */
    plotCoverage?: string
  }
  documents?: {
    title?: string
    loading?: string
    noDocuments?: string
    view?: string
    modalTitle?: string
    searchPlaceholder?: string
    searchAriaLabel?: string
    clearSearchAriaLabel?: string
    noDocumentSelected?: string
    selectDocumentHint?: string
    important?: string
    noPreviewAvailable?: string
    noPreviewHint?: string
    documentTypes?: {
      rule?: string
      map?: string
    }
    sourceLabels?: {
      lupa?: string
      lupc?: string
      lupo?: string
      lup?: string
      region?: string
      country?: string
    }
    sourceTitles?: {
      lupa?: string
      lupc?: string
      lupo?: string
      lup?: string
      region?: string
      country?: string
    }
  }
}

export interface ZoneCardProps {
  zone: Zoning
  type: ZoningType
  landId: string
  landData?: {
    std?: number
    sbd?: number
    sb?: number
    spd?: number
    sbpu?: number
    sp?: number
    vbr?: number
    ios?: number
    cos?: number
    ius?: number
    cus?: number
    ibus?: number
    mass_index?: number
  }
  zoningOverridesService?: any // Will be typed from the service
  zoningService?: any // Service for fetching documents
  /** Share of the plot covered by this zone (decimal 0..1) */
  plotCoveragePerc?: number
  translations?: ZoneCardTranslations
  /** Optional locale for date formatting (e.g., 'en', 'fr', 'de', 'it') */
  locale?: string
  /** Read-only mode - hides edit buttons and disables inputs (for public shares) */
  readOnly?: boolean
  /** Service for Zone Q&A - enables Q&A panel for general (communal) zones */
  zoneQAService?: ZoneQAService
  /** Translations for Zone Q&A panel */
  zoneQATranslations?: ZoneQATranslations
  /** Callback to transcribe voice audio for Zone Q&A */
  onTranscribeVoice?: (audioBlob: Blob) => Promise<string>
  /** Callback to report an incorrect metric value */
  onReportIncorrect?: (data: ZoningReportData) => Promise<void>
  /** Callback to fetch estimate stats for ALG justification panel */
  onFetchEstimateStats?: (
    zoneType: string,
    zoneId: string,
    metricKey: string
  ) => Promise<ZoningEstimateStats | null>
  /** Callback when the effective distance_limit value changes (for map buffer) */
  onDistanceLimitChange?: (value: number | undefined) => void
}

export const ZoneCard: React.FC<ZoneCardProps> = ({
  zone,
  type,
  landId,
  landData,
  zoningOverridesService,
  zoningService,
  plotCoveragePerc,
  translations,
  locale,
  readOnly = false,
  zoneQAService,
  zoneQATranslations,
  onTranscribeVoice,
  onReportIncorrect,
  onFetchEstimateStats,
  onDistanceLimitChange,
}) => {
  // Build a snake_case backend key → translated label map so that the
  // reference list ("Distance Limit · Building Total Height") and the
  // reference viewer modal ("Page 8" meta) render in the active language.
  // METRIC_KEY_TO_TRANSLATION_KEY maps backend keys to the camelCase
  // translation keys under metricsTable.metricLabels.
  const metricLabelMap = useMemo(() => {
    const map: Record<string, string> = {}
    const metricLabels = (translations?.metricsTable as any)?.metricLabels as
      | Record<string, string>
      | undefined
    if (metricLabels) {
      for (const [backendKey, tKey] of Object.entries(
        METRIC_KEY_TO_TRANSLATION_KEY
      )) {
        const label = metricLabels[tKey]
        if (label) map[backendKey] = label
      }
    }
    if (translations?.header?.purpose) {
      map.purpose = translations.header.purpose
    }
    if (translations?.header?.constructionArrangement) {
      map.construction_arrangement = translations.header.constructionArrangement
    }
    return map
  }, [translations])

  const referenceViewerTranslations = (translations as any)?.referenceViewer

  return (
    <Card>
      <ZoningDetailHeader
        zone={zone}
        zoningType={type}
        zoningService={zoningService}
        plotCoveragePerc={plotCoveragePerc}
        translations={{
          unknownZone:
            translations?.header?.unknownZone || translations?.unknownZone,
          status: translations?.status,
          buildingArea: translations?.header?.buildingArea,
          nonBuildingArea: translations?.header?.nonBuildingArea,
          adopted: translations?.header?.adopted,
          abrogated: translations?.header?.abrogated,
          purpose: translations?.header?.purpose,
          constructionArrangement:
            translations?.header?.constructionArrangement,
          constructionArrangementValues:
            translations?.header?.constructionArrangementValues,
          references: translations?.header?.references,
          documents: translations?.header?.documents,
          segments: translations?.header?.segments,
          askQuestion: (translations?.header as any)?.askQuestion,
          plotCoverage: translations?.header?.plotCoverage,
        }}
        documentTranslations={translations?.documents}
        referenceViewerTranslations={referenceViewerTranslations}
        metricLabelMap={metricLabelMap}
        locale={locale}
        zoneQAService={zoneQAService}
        zoneQATranslations={zoneQATranslations}
        onTranscribeVoice={onTranscribeVoice}
        getPdfProxyUrl={zoningService?.getPdfProxyUrl}
        potentialSlot={
          <ZoneMetricsTable
            zone={zone}
            zoningType={type}
            landId={landId}
            landData={landData}
            zoningOverridesService={zoningOverridesService}
            translations={translations?.metricsTable}
            readOnly={readOnly}
            onReportIncorrect={onReportIncorrect}
            onFetchEstimateStats={onFetchEstimateStats}
            onDistanceLimitChange={onDistanceLimitChange}
          />
        }
        legendSlot={<MetricsLegend translations={translations?.legend} />}
      />
    </Card>
  )
}
